import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, Volume2, VolumeX, X, Sparkles } from 'lucide-react';
import { ReelTemplate } from './ReelTemplates';
import KaleidoscopeSelector from './KaleidoscopeSelector';

interface MediaFile {
  type: 'image' | 'video';
  url: string;
  duration?: number;
}

interface ManifestationReel {
  id: string;
  title: string;
  affirmations: string[];
  subliminal_words?: string[];
  media_files: MediaFile[];
  audio_file?: string;
  template: ReelTemplate;
}

interface EnhancedManifestationPlayerProps {
  reel: ManifestationReel;
  onClose: () => void;
}

type Phase = 'selection' | 'kaleidoscope' | 'manifestation';
type AnimationType = 'flash' | 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right' | 'pan-up' | 'pan-down' | 'rotate-zoom' | 'diagonal-pan';

interface MediaWithAnimation extends MediaFile {
  animationType: AnimationType;
  duration: number;
}

// Dynamic animation types - balanced mix for constant movement
const getRandomAnimation = (): AnimationType => {
  const animations: AnimationType[] = [
    'zoom-in', 'zoom-out', 
    'pan-left', 'pan-right', 
    'pan-up', 'pan-down',
    'rotate-zoom', 'diagonal-pan',
    'flash', 'flash' // Some quick transitions
  ];
  return animations[Math.floor(Math.random() * animations.length)];
};

// Consistent duration - max 2 seconds, keeps things moving
const getDurationForAnimation = (animationType: AnimationType): number => {
  if (animationType === 'flash') {
    return Math.random() * 0.4 + 0.6; // 0.6-1.0 seconds for flashes
  }
  return Math.random() * 0.8 + 1.2; // 1.2-2.0 seconds for movements
};

export default function EnhancedManifestationPlayer({ reel, onClose }: EnhancedManifestationPlayerProps) {
  const [currentPhase, setCurrentPhase] = useState<Phase>('selection');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isShowingFullSentence, setIsShowingFullSentence] = useState(false);
  const [kaleidoscopeOption, setKaleidoscopeOption] = useState<any>(null);
  const [kaleidoscopeVideoUrl, setKaleidoscopeVideoUrl] = useState<string>('');
  const [kaleidoscopeAudioUrl, setKaleidoscopeAudioUrl] = useState<string>('');
  const [mediaWithAnimations, setMediaWithAnimations] = useState<MediaWithAnimation[]>([]);
  const [key, setKey] = useState(0); // Force re-render for animations
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isPlayingRef = useRef<boolean>(false);

  // Prepare media with random animations on mount
  useEffect(() => {
    const preparedMedia = reel.media_files.map(media => {
      const animationType = getRandomAnimation();
      const duration = getDurationForAnimation(animationType);
      return {
        ...media,
        animationType,
        duration
      };
    });
    setMediaWithAnimations(preparedMedia);
  }, [reel.media_files]);

  const currentMedia = mediaWithAnimations[currentMediaIndex];
  const currentAffirmation = reel.affirmations[currentAffirmationIndex];
  const affirmationWords = currentAffirmation?.split(' ').filter(word => word.length > 0) || [];
  const displayText = isShowingFullSentence 
    ? currentAffirmation 
    : affirmationWords[currentWordIndex] || '';
  const template = reel.template;

  // Get CSS class for animation type
  const getAnimationClass = (animation: AnimationType) => {
    const animationMap: Record<AnimationType, string> = {
      'flash': 'animate-flash-pop',
      'zoom-in': 'animate-ken-burns-zoom-in',
      'zoom-out': 'animate-ken-burns-zoom-out',
      'pan-left': 'animate-ken-burns-pan-left',
      'pan-right': 'animate-ken-burns-pan-right',
      'pan-up': 'animate-ken-burns-pan-up',
      'pan-down': 'animate-ken-burns-pan-down',
      'rotate-zoom': 'animate-ken-burns-rotate-zoom',
      'diagonal-pan': 'animate-ken-burns-diagonal'
    };
    return animationMap[animation] || '';
  };

  const getTextAnimationClass = () => {
    const textAnimationType = template.animations.text;
    
    const textAnimationMap: Record<string, string> = {
      'fade-up-glow': 'animate-text-fade-up-glow',
      'bounce-glow': 'animate-text-bounce-glow',
      'fade-glow': 'animate-text-fade-glow',
      'wave-fade': 'animate-text-wave-fade',
      'glow-expand': 'animate-text-glow-expand',
      'shimmer-fade': 'animate-text-shimmer-fade'
    };

    return textAnimationMap[textAnimationType] || 'animate-fade-in';
  };

  // Extract single powerful word for flash animations
  const getSingleWordAffirmation = (affirmation: string): string => {
    const powerWords = affirmation.split(' ').filter(word => 
      word.length > 3 && !['with', 'from', 'into', 'that', 'this', 'the'].includes(word.toLowerCase())
    );
    return powerWords[Math.floor(Math.random() * powerWords.length)] || affirmation.split(' ')[0];
  };

  // Get varied typography style for single words
  const getTypographyStyle = (): string => {
    const styles = [
      'font-serif tracking-widest uppercase',
      'font-bold tracking-tight italic',
      'font-extrabold tracking-wider',
      'font-light tracking-widest text-8xl',
      'font-black tracking-normal'
    ];
    return styles[currentMediaIndex % styles.length];
  };

  const playNext = () => {
    if (!isPlayingRef.current || mediaWithAnimations.length === 0) {
      return;
    }

    // Determine next state to calculate correct duration
    let willShowFullSentence = false;
    if (isShowingFullSentence) {
      willShowFullSentence = false; // Next will be first word of next affirmation
    } else {
      const words = reel.affirmations[currentAffirmationIndex]?.split(' ').filter(w => w.length > 0) || [];
      willShowFullSentence = currentWordIndex >= words.length - 1; // Next will be full sentence
    }

    // Calculate duration based on what we're about to show
    const duration = willShowFullSentence 
      ? Math.random() * 0.8 + 1.5  // 1.5-2.3 seconds for full sentence
      : Math.random() * 0.3 + 0.5; // 0.5-0.8 seconds for individual words
    
    // Update state
    setCurrentMediaIndex(prevIndex => (prevIndex + 1) % mediaWithAnimations.length);
    
    if (isShowingFullSentence) {
      setCurrentAffirmationIndex(prev => (prev + 1) % reel.affirmations.length);
      setCurrentWordIndex(0);
      setIsShowingFullSentence(false);
    } else {
      const words = reel.affirmations[currentAffirmationIndex]?.split(' ').filter(w => w.length > 0) || [];
      if (currentWordIndex < words.length - 1) {
        setCurrentWordIndex(prev => prev + 1);
      } else {
        setIsShowingFullSentence(true);
      }
    }
    
    setKey(prev => prev + 1);
    
    // Schedule next transition
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    timerRef.current = setTimeout(playNext, duration * 1000);
  };

  const startManifestationPhase = () => {
    setCurrentPhase('manifestation');
    setCurrentMediaIndex(0);
    setCurrentAffirmationIndex(0);
    setKey(0);
    setIsPlaying(true);
    isPlayingRef.current = true;
    
    // Start audio immediately
    if (audioRef.current && reel.audio_file) {
      audioRef.current.loop = true;
      audioRef.current.play().catch(() => {});
    }
    
    // Schedule first transition
    if (mediaWithAnimations.length > 0) {
      const firstDuration = mediaWithAnimations[0]?.duration || 3;
      
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      timerRef.current = setTimeout(() => {
        playNext();
      }, firstDuration * 1000);
    }
  };

  const startKaleidoscopePhase = () => {
    setCurrentPhase('kaleidoscope');
    setIsPlaying(true);
    
    if (videoRef.current) {
      videoRef.current.play().catch(console.error);
    }
  };

  const handleKaleidoscopeSelect = async (option: any) => {
    setKaleidoscopeOption(option);
    
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data: videoData } = await supabase.storage
        .from('kaleidoscope-videos')
        .createSignedUrl(option.video_path, 3600);
      
      if (videoData?.signedUrl) {
        setKaleidoscopeVideoUrl(videoData.signedUrl);
      }

      if (option.audio_path) {
        const { data: audioData } = await supabase.storage
          .from('kaleidoscope-audio')
          .createSignedUrl(option.audio_path, 3600);
        
        if (audioData?.signedUrl) {
          setKaleidoscopeAudioUrl(audioData.signedUrl);
        }
      }
      
      startKaleidoscopePhase();
    } catch (error) {
      console.error('Error loading kaleidoscope:', error);
      startManifestationPhase();
    }
  };

  const handleSkipKaleidoscope = () => {
    startManifestationPhase();
  };

  const handleKaleidoscopeEnd = () => {
    startManifestationPhase();
  };

  const togglePlay = () => {
    if (currentPhase === 'selection') return;
    
    if (isPlaying) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (audioRef.current) audioRef.current.pause();
      if (videoRef.current) videoRef.current.pause();
    } else {
      setIsPlaying(true);
      isPlayingRef.current = true;
      if (audioRef.current) audioRef.current.play().catch(console.error);
      if (videoRef.current) videoRef.current.play().catch(console.error);
      
      if (currentMedia) {
        const duration = currentMedia.duration || 3;
        timerRef.current = setTimeout(playNext, duration * 1000);
      }
    }
  };

  const toggleAudio = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const resetPlayback = () => {
    setCurrentMediaIndex(0);
    setCurrentAffirmationIndex(0);
    setCurrentWordIndex(0);
    setIsShowingFullSentence(false);
    setIsPlaying(false);
    isPlayingRef.current = false;
    setKey(0);
    
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.pause();
    }
    
    // Restart playback
    setTimeout(() => {
      setIsPlaying(true);
      isPlayingRef.current = true;
      if (audioRef.current && reel.audio_file) {
        audioRef.current.play().catch(console.error);
      }
      if (mediaWithAnimations.length > 0) {
        const duration = 0.7; // Quick start for first word
        timerRef.current = setTimeout(playNext, duration * 1000);
      }
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
    };
  }, []);

  // Selection phase
  if (currentPhase === 'selection') {
    return (
      <KaleidoscopeSelector
        onSelect={handleKaleidoscopeSelect}
        onSkip={handleSkipKaleidoscope}
      />
    );
  }

  // Kaleidoscope phase
  if (currentPhase === 'kaleidoscope' && kaleidoscopeVideoUrl) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <video
          ref={videoRef}
          src={kaleidoscopeVideoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          onEnded={handleKaleidoscopeEnd}
          muted={isMuted}
        />
        
        {kaleidoscopeAudioUrl && (
          <audio
            src={kaleidoscopeAudioUrl}
            autoPlay
            muted={isMuted}
          />
        )}

        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="text-center text-primary-foreground z-10">
            <Sparkles className="w-16 h-16 mx-auto mb-4 animate-pulse text-primary" />
            <h2 className="text-3xl font-bold mb-2">{reel.title}</h2>
            <p className="text-lg opacity-80">Preparing your manifestation...</p>
          </div>
        </div>

        <Button
          onClick={handleKaleidoscopeEnd}
          variant="outline"
          className="absolute bottom-8 right-8 z-20"
        >
          Skip
        </Button>

        <Button
          onClick={() => {
            if (videoRef.current) {
              videoRef.current.pause();
              videoRef.current.src = '';
            }
            onClose();
          }}
          size="icon"
          variant="ghost"
          className="absolute top-4 right-4 z-50 bg-background/80 backdrop-blur-sm hover:bg-primary/20 hover:text-primary"
        >
          <X className="w-6 h-6" />
        </Button>
      </div>
    );
  }

  // Manifestation phase
  return (
    <div className="fixed inset-0 z-50 bg-background overflow-hidden">
      {/* Background media with dynamic animations */}
      <div className="absolute inset-0 overflow-hidden">
        {currentMedia && (
          <div
            key={`${currentMediaIndex}-${key}`}
            className={`absolute inset-0 w-full h-full ${getAnimationClass(currentMedia.animationType)}`}
            style={{
              backgroundImage: currentMedia.type === 'image' ? `url(${currentMedia.url})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animationDuration: `${currentMedia.duration}s`,
              animationFillMode: 'forwards'
            }}
          >
            {currentMedia.type === 'video' && (
              <video
                src={currentMedia.url}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                muted
                loop={false}
              />
            )}
          </div>
        )}
        
        {/* Brand gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-transparent to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/10 to-accent/10" />
      </div>

      {/* Background audio */}
      {reel.audio_file && (
        <audio ref={audioRef} src={reel.audio_file} muted={isMuted} />
      )}

      {/* Content overlay */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-8">
        {/* Title */}
        <div className="absolute top-8 left-0 right-0 text-center">
          <h1 className="text-2xl md:text-4xl font-bold text-primary-foreground drop-shadow-2xl">
            {reel.title}
          </h1>
        </div>

        {/* Affirmation - individual words then full sentence */}
        {displayText && (
          <div
            key={`affirmation-${currentAffirmationIndex}-${currentWordIndex}-${isShowingFullSentence}-${key}`}
            className={`text-center max-w-4xl ${
              isShowingFullSentence ? 'animate-scale-in' : 'animate-fade-in'
            }`}
          >
            {isShowingFullSentence ? (
              // Full sentence with zoom effect
              <div className="bg-background/60 backdrop-blur-sm rounded-2xl p-8 border border-primary/20">
                <p className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight px-4 drop-shadow-2xl [text-shadow:_0_0_30px_rgb(255_255_255_/_40%)]">
                  {displayText}
                </p>
              </div>
            ) : (
              // Single word, bold typography
              <div className={`${getTypographyStyle()}`}>
                <p className="text-6xl md:text-8xl lg:text-9xl text-foreground leading-none drop-shadow-2xl [text-shadow:_0_0_50px_rgb(255_255_255_/_60%)] font-black uppercase tracking-wider">
                  {displayText}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Subliminal words (subtle, brief flashes) */}
        {reel.subliminal_words && reel.subliminal_words.length > 0 && (
          <div 
            key={`subliminal-${key}`}
            className="absolute bottom-32 left-0 right-0 text-center opacity-30 animate-pulse"
          >
            <p className="text-sm text-primary-foreground/60">
              {reel.subliminal_words[currentMediaIndex % reel.subliminal_words.length]}
            </p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="absolute bottom-24 left-0 right-0 px-8">
          <div className="flex gap-1 justify-center">
            {mediaWithAnimations.map((_, index) => (
              <div
                key={index}
                className={`h-1 w-2 rounded-full transition-all duration-300 ${
                  index < currentMediaIndex
                    ? 'bg-primary'
                    : index === currentMediaIndex
                    ? 'bg-primary animate-pulse'
                    : 'bg-primary/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background/90 to-transparent">
        <div className="flex items-center justify-center gap-4">
          <Button
            onClick={togglePlay}
            size="icon"
            variant="ghost"
            className="hover:bg-primary/20 hover:text-primary"
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </Button>
          
          <Button
            onClick={resetPlayback}
            size="icon"
            variant="ghost"
            className="hover:bg-primary/20 hover:text-primary"
          >
            <RotateCcw className="w-6 h-6" />
          </Button>
          
          <Button
            onClick={toggleAudio}
            size="icon"
            variant="ghost"
            className="hover:bg-primary/20 hover:text-primary"
          >
            {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Close button */}
      <Button
        onClick={() => {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
          }
          onClose();
        }}
        size="icon"
        variant="ghost"
        className="absolute top-4 right-4 z-50 hover:bg-primary/20 hover:text-primary bg-background/80 backdrop-blur-sm"
      >
        <X className="w-6 h-6" />
      </Button>
    </div>
  );
}
